/**
 * Price History Section Switch
 * 
 * Control: Current implementation (PriceHistory with its own heading)
 * Variation: New design using ContentSectionTemplate
 */

import { useCallback, forwardRef } from 'react';
import { Box } from '@mui/material';
import { InView } from 'react-intersection-observer';
import { AllowedExperimentKey } from '@tenants/types';
import { AnalyticsController } from 'analytics/analytics-controller';
import { GTM_VIEW_ADDRESS_PAGE_PRICE_HISTORY_TAB } from 'constants/events';
import { ExperimentalSection } from 'ui/experimental-section';
import { PriceHistoryTimeline, type PriceHistoryTimelineRecordProps } from 'ui/price-history';
import { ContentSectionTemplate } from 'components/atomic/templates/content-section';
import PriceHistory from 'components/pages/address/public/components/price-history';
import type { SaleHistory, RentalHistory } from 'components/pages/address/public/components/price-history';
import type { ComplianceRequirement } from 'types/compliance';

// ============================================================================
// Types
// ============================================================================

export interface PriceHistorySectionProps {
  /** Section ID for scroll targeting */
  id: string;
  /** Current listing ID */
  currentListingId: number;
  /** Whether this is a rental listing */
  isRental: boolean;
  /** Sale history records */
  saleHistory?: readonly SaleHistory[];
  /** Rental history records */
  rentalHistory?: readonly RentalHistory[];
  /** Synthetic "today" record derived from the current listing */
  currentListingData: PriceHistoryTimelineRecordProps;
  /** Callback for compliance requirement click */
  onComplianceRequirementClick?: (complianceRequirement: ComplianceRequirement, providerId: number) => void;
  /** Callback for contact for price click */
  onContactForPriceClick?: () => void;
  /** Callback for view listing click */
  onViewListingClick?: (addressPath: string, listingId: number) => void;
}

// ============================================================================
// Shared Hook
// ============================================================================

function usePriceHistoryTracking() {
  return useCallback((isVisible: boolean) => {
    AnalyticsController.trackEventConditionally(
      { event: GTM_VIEW_ADDRESS_PAGE_PRICE_HISTORY_TAB },
      isVisible
    );
  }, []);
}

// ============================================================================
// Control (Current Implementation)
// ============================================================================

const PriceHistorySectionControl = forwardRef<HTMLDivElement, PriceHistorySectionProps>(
  function PriceHistorySectionControl(
    {
      id,
      currentListingId,
      isRental,
      saleHistory,
      rentalHistory,
      currentListingData: _currentListingData,
      onComplianceRequirementClick,
      onContactForPriceClick,
      onViewListingClick,
    },
    ref
  ) {
    const trackPriceHistoryView = usePriceHistoryTracking();

    // PriceHistory component has its own <h2> heading and card styling
    return (
      <Box component="section" id={id} sx={{ scrollMarginTop: 'var(--scroll-offset, 150px)' }}>
        <InView onChange={trackPriceHistoryView} triggerOnce>
          <PriceHistory
            ref={ref}
            currentListingId={currentListingId}
            isRental={isRental}
            saleHistory={saleHistory}
            rentalHistory={rentalHistory}
            onComplianceRequirementClick={onComplianceRequirementClick}
            onContactForPriceClick={onContactForPriceClick}
            onViewListingClick={onViewListingClick}
          />
        </InView>
      </Box>
    );
  }
);

// ============================================================================
// Variation (New Design with Templates)
// ============================================================================

const PriceHistorySectionVariation = forwardRef<HTMLDivElement, PriceHistorySectionProps>(
  function PriceHistorySectionVariation(
    {
      id,
      currentListingId,
      isRental,
      saleHistory,
      rentalHistory,
      currentListingData,
      onComplianceRequirementClick,
      onViewListingClick,
    },
    ref
  ) {
    const trackPriceHistoryView = usePriceHistoryTracking();

    // PriceHistory component renders its own <h2> heading and border styling internally
    return (
      <ContentSectionTemplate id={id} hideHeading withCard={false}>
        <InView onChange={trackPriceHistoryView} triggerOnce>
          <PriceHistoryTimeline
            ref={ref}
            currentListingId={currentListingId}
            isRental={isRental}
            saleHistory={saleHistory}
            rentalHistory={rentalHistory}
            currentListingData={currentListingData}
            onComplianceRequirementClick={onComplianceRequirementClick}
            onViewListingClick={onViewListingClick}
          />
        </InView>
      </ContentSectionTemplate>
    );
  }
);

// ============================================================================
// Switch
// ============================================================================

export const PriceHistorySectionSwitch = forwardRef<HTMLDivElement, PriceHistorySectionProps>(
  function PriceHistorySectionSwitch(props, ref) {
    return (
      <ExperimentalSection
        experimentKey={AllowedExperimentKey.STICKY_HEADER_TABS}
        control={<PriceHistorySectionControl {...props} ref={ref} />}
        variation={<PriceHistorySectionVariation {...props} ref={ref} />}
      />
    );
  }
);
